import { useState, useEffect, useRef } from 'react';
import { ChevronDown, Check, Search } from 'lucide-react';
import { Tooltip } from 'primereact/tooltip';

const TaskCategoryDropdown = ({ value, onChange, tasks, accent }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleOpen = () => {
    if (isOpen) {
      setIsOpen(false);
    } else {
      setIsOpen(true);
      setSearch('');
    }
  };

  const handleSelect = (taskName) => {
    onChange(taskName);
    setIsOpen(false);
  };

  const displayLabel = value
    ? (tasks.find(t => t.name === value)?.name || value)
    : '— Tidak Dikategorikan (Uncategorized) —';

  const filtered = tasks.filter(t =>
    !search || t.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={handleOpen}
        className="w-full flex items-center justify-between gap-2 px-3.5 py-2.5 rounded-lg bg-gray-50 text-sm font-inter transition-all duration-200 outline-none truncate"
        style={{
          border: `1.5px solid ${isOpen ? accent : '#e2e8f0'}`,
          boxShadow: isOpen ? `0 0 0 3px ${accent}20` : 'none',
          color: value ? '#0f172a' : '#94a3b8',
          fontFamily: "'Inter', sans-serif",
        }}
      >
        <span className="truncate text-sm" style={{ fontWeight: value ? '600' : '400' }}>
          {displayLabel}
        </span>
        <ChevronDown
          size={14}
          color="#94a3b8"
          className="shrink-0 transition-transform duration-200"
          style={{ transform: isOpen ? 'rotate(180deg)' : 'none' }}
        />
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div
          className="absolute top-[calc(100%+4px)] left-0 right-0 z-[950] bg-white rounded-xl border border-slate-200 flex flex-col overflow-hidden"
          style={{ maxHeight: '240px', boxShadow: '0 12px 32px rgba(0,0,0,0.14)', animation: 'dropFadeIn 0.15s ease' }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Search */}
          <div className="p-2 border-b border-slate-100 bg-slate-50">
            <div className="relative flex items-center">
              <Search size={12} color="#94a3b8" className="absolute left-2.5" />
              <input
                autoFocus
                type="text"
                placeholder="Cari Task..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-7 pr-2.5 py-1.5 border border-slate-200 rounded-md text-xs text-slate-900 outline-none transition-colors duration-200"
                style={{ fontFamily: "'Inter', sans-serif" }}
                onFocus={(e) => { e.target.style.borderColor = accent; }}
                onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; }}
              />
            </div>
          </div>

          {/* Options List */}
          <div className="overflow-y-auto flex-1 p-1">
            <Tooltip
              target=".taskcatdrop-option-tooltip"
              position="right"
              mouseTrack
              mouseTrackTop={15}
              pt={{
                text: {
                  style: {
                    fontSize: '11px', padding: '6px 10px', fontWeight: '500',
                    maxWidth: '220px', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  }
                }
              }}
            />

            {/* Uncategorized option */}
            <div
              className="taskcatdrop-option-tooltip flex items-center justify-between px-2.5 py-2 rounded-md cursor-pointer"
              data-pr-tooltip="Tidak Dikategorikan (Uncategorized)"
              onClick={() => handleSelect('')}
              style={{ background: !value ? `${accent}15` : 'transparent', fontWeight: !value ? '700' : '400' }}
              onMouseEnter={(e) => { if (value) e.currentTarget.style.background = '#f8fafc'; }}
              onMouseLeave={(e) => { if (value) e.currentTarget.style.background = 'transparent'; }}
            >
              <span className="text-slate-700 text-[13px]">— Tidak Dikategorikan (Uncategorized) —</span>
              {!value && <Check size={12} color={accent} className="shrink-0" />}
            </div>

            {/* Task options */}
            {filtered.map((t) => (
              <div
                key={t.id || t.name}
                className="taskcatdrop-option-tooltip flex items-center justify-between px-2.5 py-2 rounded-md cursor-pointer"
                data-pr-tooltip={t.name}
                onClick={() => handleSelect(t.name)}
                style={{
                  background: value === t.name ? `${accent}15` : 'transparent',
                  fontWeight: value === t.name ? '700' : '400',
                }}
                onMouseEnter={(e) => { if (value !== t.name) e.currentTarget.style.background = '#f8fafc'; }}
                onMouseLeave={(e) => { if (value !== t.name) e.currentTarget.style.background = 'transparent'; }}
              >
                <span className="text-slate-700 text-[13px] truncate">{t.name}</span>
                {value === t.name && <Check size={12} color={accent} className="shrink-0" />}
              </div>
            ))}

            {filtered.length === 0 && (
              <div className="py-3 text-center text-[11.5px] text-slate-400">
                Tidak ditemukan
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskCategoryDropdown;
